import { useState } from "react";
import { UserCheck, Loader2, CheckCircle2 } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * One-click backfill for legacy leads that lost their `assigned_office_staff`
 * link. Scans history + created_by hints and re-links each lead to the correct
 * telecaller. Renders the per-telecaller report so admin can eyeball the fix.
 */
export default function TelecallerBackfillCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/backfill-telecaller");
      setResult(r.data);
      toast.success(`Fixed ${r.data.fixed} lead(s) · refreshed ${r.data.cached_refreshed} name/phone cache`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setRunning(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="telecaller-backfill-card">
      <div className="flex items-center gap-2 mb-2">
        <UserCheck className="w-4 h-4 text-amber-400"/>
        <div className="font-heading text-white text-sm">Telecaller Backfill</div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3 leading-relaxed">
        Fixes legacy leads where the assigned telecaller reads <span className="font-mono text-slate-400">--</span>
        or empty. Scans lead history for "by NAME" / "Verified · NAME" / created-by
        hints and re-links them. Also refreshes cached name + phone across every lead.
      </div>
      <Button
        onClick={run}
        disabled={running}
        data-testid="run-backfill-btn"
        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full text-xs disabled:opacity-60">
        {running
          ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> Scanning leads…</>
          : <><UserCheck className="w-3.5 h-3.5 mr-1.5"/> Run Backfill</>}
      </Button>
      {result && (
        <div className="mt-3 space-y-2" data-testid="backfill-report">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-slate-950/60 border border-slate-800 p-2">
              <div className="font-display text-lg text-emerald-400">{result.fixed}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Fixed</div>
            </div>
            <div className="rounded-md bg-slate-950/60 border border-slate-800 p-2">
              <div className="font-display text-lg text-slate-300">{result.cached_refreshed}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Refreshed</div>
            </div>
            <div className="rounded-md bg-slate-950/60 border border-slate-800 p-2">
              <div className="font-display text-lg text-slate-500">{result.unmatched}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Unmatched</div>
            </div>
          </div>
          {result.per_telecaller?.length > 0 && (
            <div className="rounded-md border border-slate-800 bg-slate-950/60 p-2 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">Per telecaller</div>
              {result.per_telecaller.map((r) => (
                <div key={r.name} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-300 truncate mr-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0"/> {r.name}
                  </span>
                  <span className="font-mono-alt text-emerald-300 font-bold">{r.count}</span>
                </div>
              ))}
            </div>
          )}
          {result.fixed === 0 && result.unmatched === 0 && (
            <div className="text-[11px] text-emerald-400 text-center">✓ Every lead already has an assigned telecaller.</div>
          )}
        </div>
      )}
    </div>
  );
}
